import * as React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { registerForLocalization, registerForIntl, provideLocalizationService, provideIntlService } from '@progress/kendo-react-intl';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Skeleton } from '@progress/kendo-react-indicators';
import { OverviewPage, StackLayout, Text, Colors } from '../../../ui';
import { StudentCompetencyList } from '../../shared/student-competency';
import { loadStudentOverviewPage, selectStudentOverviewPageTab } from '../actions';
import { StudentCycles, StudentHoursList } from '.';
import { withRouter } from '../../../components/withRouter';
import { EmptyFormatter, InlineBadge, ValueItem } from '../../shared';
import { getStudentStatusColor } from '../../../utils/studentStatusColors';
import { DateFormatter } from '../../shared/components';

const Tabs = styled(TabStrip)`
  & > .k-content.k-state-active {
    display: none;
  }
  & > .k-tabstrip-items-wrapper .k-item::after {
    border-bottom-width: 2px;
  }
`;

const CaptionText = styled(Text).attrs(() => ({
  textColor: Colors.GRAY_50,
  variant: 'caption'
}))``;

class StudentOverviewPage extends React.PureComponent {

  constructor() {
    super();
    this.handleBackClick = this.handleBackClick.bind(this);
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
  }

  async componentDidMount() {
    if (this.props.params?.studentId) {
      this.props.loadStudentOverviewPage(this.props.params.studentId);
    }
  }

  render() {
    const { student, hoursSummary, loading } = this.props;
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    return (
      <OverviewPage
        title={loading
          ? <Skeleton shape='text' style={{ width: 300, height: 28 }} />
          : student?.fullName
        }
        breadcrumbItems={[
          {
            id: 'students',
            text: localizationService.toLanguageString('student.residents')
          },
          {
            id: 'student',
            text: localizationService.toLanguageString('student.resident'),
            disabled: true
          }
        ]}
        onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
        onBackClick={this.handleBackClick}
        summary={
          <StackLayout rowGap='8px'>
            <ValueItem
              label={localizationService.toLanguageString('student.studyProgramLabel')}
              labelWidth='115px'
              loading={loading}
            >
              <Text variant='body2' overflowWrap={true}>
                {`${student?.number} - ${student?.studyProgram}`}
              </Text>
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('myStudies.studyStatus')}
              labelWidth='115px'
              loading={loading}
            >
              <StackLayout orientation='horizontal' align='left' valign='center' columnGap='8px'>
                <InlineBadge themeColor={getStudentStatusColor(student?.status)}>
                  {localizationService.toLanguageString(`studentStatus.${student?.status}`)}
                </InlineBadge>
                {student?.statusDate &&
                  <CaptionText>
                    {`(${localizationService.toLanguageString('residentCycles.changeDate')} ${DateFormatter(student.statusDate, intlService, true)})`}
                  </CaptionText>
                }
              </StackLayout>
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('student.year')}
              labelWidth='115px'
              loading={loading}
            >
              {student?.studyYear}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('studentHours.hoursLabel')}
              labelWidth='115px'
              loading={loading}
            >
              {EmptyFormatter(hoursSummary
                ? `${hoursSummary.approvedMinutes} / ${hoursSummary.targetMinutes} ${localizationService.toLanguageString('studentHours.minutesUnit')}`
                : null
              )}
            </ValueItem>
          </StackLayout>
        }
        tabs={
          <Tabs selected={this.props.selectedTab} onSelect={this.props.selectStudentOverviewPageTab}>
            <TabStripTab title={localizationService.toLanguageString('student.cycles')} />
            <TabStripTab title={localizationService.toLanguageString('student.competencies')} />
            <TabStripTab title={localizationService.toLanguageString('studentHours.tabTitle')} />
          </Tabs>
        }
      >
        {this.props.selectedTab === 0 && <StudentCycles studentId={this.props.params.studentId}/>}
        {this.props.selectedTab === 1 && <StudentCompetencyList studentId={this.props.params.studentId} onRowClick={(e) => this.props.navigate(`/students/${this.props.params.studentId}/competencies/${e.dataItem.id}/overview`)} rowLink={(dataItem) => `/students/${this.props.params.studentId}/competencies/${dataItem.id}/overview`}/>}
        {this.props.selectedTab === 2 && <StudentHoursList studentId={this.props.params.studentId}/>}
      </OverviewPage>
    );
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'students') {
      this.props.navigate('/students');
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }
}

registerForIntl(StudentOverviewPage);
registerForLocalization(StudentOverviewPage);

const mapStateToProps = state => ({
  student: state.studentOverviewPage.student,
  hoursSummary: state.studentOverviewPage.hoursSummary,
  loading: state.studentOverviewPage.loading,
  selectedTab: state.studentOverviewPage.selectedTab
});

const mapDispatchToProps = dispatch => ({
  loadStudentOverviewPage: (payload) => dispatch(loadStudentOverviewPage(payload)),
  selectStudentOverviewPageTab: (payload) => dispatch(selectStudentOverviewPageTab(payload))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(StudentOverviewPage));
